<template>
	<div v-if="toastNotifications.length > 0 && currentToast" class="toast-container">
		<v-card
			class="toast-card"
			:class="{
				'new-notification': showNewNotificationAnimation,
				collapsed: isCollapsed,
				'has-carousel': !isCollapsed,
			}"
			:style="{ '--notification-color': toastColor, '--contrast-color': contrastColor }"
			rounded="lg"
			elevation="4"
			:ripple="false"
			v-bind="isCollapsed ? { onClick: handleToastClick } : {}"
			@mouseenter="handleMouseEnter"
			@mouseleave="handleMouseLeave"
		>
			<v-alert :color="toastColor" :title="currentToast.label" density="compact" rounded="lg" variant="flat">
				<template #title>
					<div class="d-flex align-center justify-space-between" style="width: 100%">
						<v-icon class="ma-1" :icon="toastIcon" size="small"></v-icon>
						<span class="toast-title text-truncate">{{ currentToast.label }}</span>
						<div class="d-flex align-center">
							<v-btn v-if="isCollapsed" variant="text" size="default" icon="mdi-chevron-down" />
							<v-tooltip
								v-if="!isCollapsed"
								:text="
									isPinned
										? symbolsStore.GUILabel('6bd98cad-79e1-4a5b-9962-a449df626e5b')
										: symbolsStore.GUILabel('ba46dc1f-bed3-450a-b9b0-fdb8a3794d80')
								"
								open-delay="500"
								location="bottom"
							>
								<template #activator="{ props }">
									<v-btn
										class="pin-btn"
										:class="{ 'pin-btn--active': isPinned }"
										variant="text"
										size="default"
										:icon="isPinned ? 'mdi-pin' : 'mdi-pin-outline'"
										v-bind="props"
										@click.stop="togglePin"
									/>
								</template>
							</v-tooltip>
							<v-btn
								v-if="!isCollapsed"
								variant="text"
								size="default"
								icon="mdi-chevron-up"
								@click.stop="collapseToast"
							/>
						</div>
					</div>
				</template>

				<template #text>
					<div class="toast-content" :class="{ 'collapsed-content': isCollapsed }">
						<div>
							{{ currentToast.description }}
						</div>
						<div class="toast-counter">
							<span :style="{ visibility: toastNotifications.length > 1 ? 'visible' : 'hidden' }">
								{{ currentIndex + 1 }}/{{ toastNotifications.length }}
							</span>
						</div>
					</div>
				</template>
			</v-alert>

			<!-- Progress bar for collapse timer -->
			<div
				v-if="!isCollapsed && (!isPinned || pendingNotificationQueue.length > 0)"
				class="collapse-progress-bar"
				:style="{
					'--progress-color': toastColor,
					'--progress-width': progressWidth + '%',
					'--animation-duration': progressAnimationDuration + 'ms',
				}"
			></div>
			<!-- Carousel navigation zones -->
			<div
				v-if="!isCollapsed"
				class="carousel-zone carousel-zone--left"
				:class="{ 'carousel-zone--disabled': toastNotifications.length <= 1 || currentIndex === 0 }"
				@click.stop="previousToast"
			>
				<v-icon size="32" icon="mdi-chevron-left" />
			</div>
			<div
				v-if="!isCollapsed"
				class="carousel-zone carousel-zone--right"
				:class="{
					'carousel-zone--disabled':
						toastNotifications.length <= 1 || currentIndex === toastNotifications.length - 1,
				}"
				@click.stop="nextToast"
			>
				<v-icon size="32" icon="mdi-chevron-right" />
			</div>
		</v-card>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect, onMounted, onUnmounted, nextTick } from "vue";
import { useGraphStore } from "../../../store/graph";
import { useSymbolsStore } from "../../../store/symbols";

const graphStore = useGraphStore();
const symbolsStore = useSymbolsStore();

// Constants
const DEFAULT_TOAST_COLOR = "#4db6ac";
const COLLAPSE_DURATION = 10000; // 10 seconds
const PROGRESS_UPDATE_INTERVAL = 50; // 50ms
const NEW_NOTIFICATION_ANIMATION_DURATION = 750; // 750ms

const currentIndex = ref(0);
const showNewNotificationAnimation = ref(false);
const lastNotificationCount = ref(0);
const isCollapsed = ref(false);
const collapseTimeout = ref<NodeJS.Timeout | null>(null);
const isHovered = ref(false);
const progressWidth = ref(100);
const progressAnimationDuration = ref(COLLAPSE_DURATION);
const progressInterval = ref<NodeJS.Timeout | null>(null);
const progressStartTime = ref<number>(0);
const progressRemainingTime = ref<number>(COLLAPSE_DURATION);
const isPinned = ref(true);
const isSwitchingToNewNotification = ref(false);
const pendingNotificationQueue = ref<string[]>([]);
const pinnedDisplayTimeout = ref<NodeJS.Timeout | null>(null);
const currentNotificationDisplayedAt = ref<number>(Date.now());

// Use a ref to store filtered notifications to avoid frequent recomputation
const toastNotifications = ref<any[]>([]);

// Update notifications when the source changes, but in a more controlled way
watchEffect(() => {
	const newToasts = graphStore.descriptor.notifications
		.filter((n) => n.display === "toast")
		.sort((a, b) => b.timestamp - a.timestamp);

	const currentIds = toastNotifications.value.map((n) => n.id).join(",");
	const newIds = newToasts.map((n) => n.id).join(",");

	if (currentIds !== newIds) {
		toastNotifications.value = newToasts;
	}
});

const currentToast = computed(() => {
	if (toastNotifications.value.length === 0) return null;
	return toastNotifications.value[currentIndex.value];
});

const toastColor = computed(() => currentToast.value?.color || DEFAULT_TOAST_COLOR);

// Helper function to calculate relative luminance of a color
function getLuminance(hexColor: string): number {
	// Remove # if present
	const hex = hexColor.replace("#", "");

	// Parse RGB values
	let r = parseInt(hex.substring(0, 2), 16) / 255;
	let g = parseInt(hex.substring(2, 4), 16) / 255;
	let b = parseInt(hex.substring(4, 6), 16) / 255;

	// Apply gamma correction
	r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
	g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
	b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

	// Calculate luminance
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Computed property to determine contrast color based on background luminance
const contrastColor = computed(() => {
	const luminance = getLuminance(toastColor.value);
	// Use dark text/elements for light backgrounds, light for dark backgrounds
	return luminance > 0.4 ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.9)";
});

const contrastColorDisabled = computed(() => {
	const luminance = getLuminance(toastColor.value);
	return luminance > 0.4 ? "rgba(0, 0, 0, 0.35)" : "rgba(255, 255, 255, 0.4)";
});

const toastIcon = computed(() => {
	if (!currentToast.value) return "";
	const icon = currentToast.value.icon || "lightbulb-on";
	return icon.startsWith("mdi-") ? icon : `mdi-${icon}`;
});

// Helper function to reset all timers and progress
function resetTimersAndProgress() {
	clearCollapseTimer();
	clearProgressInterval();
	progressWidth.value = 100;
}

// Helper function to reset toast state
function resetToastState() {
	isCollapsed.value = false;
	isHovered.value = false;
}

// Function to start collapse timer
function startCollapseTimer() {
	// Reset progress
	resetTimersAndProgress();

	// Don't start timer if pinned
	if (isPinned.value) {
		return;
	}

	progressStartTime.value = Date.now();
	progressRemainingTime.value = COLLAPSE_DURATION;
	progressAnimationDuration.value = COLLAPSE_DURATION;

	// Start progress animation
	startProgressAnimation();

	collapseTimeout.value = setTimeout(() => {
		if (!isHovered.value && !isPinned.value && !isCollapsed.value) {
			if (pendingNotificationQueue.value.length > 0) {
				showNextPendingNotification();
			} else {
				isCollapsed.value = true;
				clearProgressInterval();
			}
		} else if (isHovered.value) {
			startCollapseTimer();
		}
	}, COLLAPSE_DURATION);
}

// Function to clear collapse timer
function clearCollapseTimer() {
	if (collapseTimeout.value) {
		clearTimeout(collapseTimeout.value);
		collapseTimeout.value = null;
	}
}

// Function to start progress animation
function startProgressAnimation() {
	clearProgressInterval();

	progressInterval.value = setInterval(() => {
		// Don't animate progress while hovered, or while pinned with no pending notifications
		if (isHovered.value || (isPinned.value && pendingNotificationQueue.value.length === 0)) {
			return;
		}

		const elapsed = Date.now() - progressStartTime.value;
		const remaining = Math.max(0, progressRemainingTime.value - elapsed);
		const newWidth = (remaining / progressRemainingTime.value) * 100;

		progressWidth.value = Math.max(0, newWidth);

		if (remaining <= 0) {
			clearProgressInterval();
		}
	}, PROGRESS_UPDATE_INTERVAL);
}

// Function to clear progress interval
function clearProgressInterval() {
	if (progressInterval.value) {
		clearInterval(progressInterval.value);
		progressInterval.value = null;
	}
}

// Show the next notification from the pending queue
function showNextPendingNotification() {
	while (pendingNotificationQueue.value.length > 0) {
		const nextId = pendingNotificationQueue.value.shift()!;
		const idx = toastNotifications.value.findIndex((n) => n.id === nextId);
		if (idx !== -1) {
			isSwitchingToNewNotification.value = true;
			currentIndex.value = idx;
			currentNotificationDisplayedAt.value = Date.now();

			showNewNotificationAnimation.value = false;
			requestAnimationFrame(() => {
				showNewNotificationAnimation.value = true;
				setTimeout(() => {
					showNewNotificationAnimation.value = false;
				}, NEW_NOTIFICATION_ANIMATION_DURATION);
			});

			if (isPinned.value) {
				if (pendingNotificationQueue.value.length > 0) {
					startPinnedDisplayTimer();
				}
			} else {
				startCollapseTimer();
			}
			return;
		}
	}
	// No valid pending notifications left - collapse if not pinned
	if (!isPinned.value) {
		isCollapsed.value = true;
		clearProgressInterval();
	}
}

function startPinnedDisplayTimer(duration: number = COLLAPSE_DURATION) {
	clearPinnedDisplayTimer();
	progressStartTime.value = Date.now();
	progressRemainingTime.value = duration;
	progressAnimationDuration.value = duration;
	progressWidth.value = 100;
	startProgressAnimation();
	pinnedDisplayTimeout.value = setTimeout(() => {
		if (pendingNotificationQueue.value.length > 0) {
			showNextPendingNotification();
		} else {
			clearProgressInterval();
			progressWidth.value = 100;
		}
	}, duration);
}

function clearPinnedDisplayTimer() {
	if (pinnedDisplayTimeout.value) {
		clearTimeout(pinnedDisplayTimeout.value);
		pinnedDisplayTimeout.value = null;
	}
	if (isPinned.value) {
		clearProgressInterval();
		progressWidth.value = 100;
	}
}

// Handle mouse enter - reset but don't start countdown
function handleMouseEnter() {
	isHovered.value = true;
	// Reset progress to full but don't start countdown
	if (!isCollapsed.value) {
		resetTimersAndProgress();
		progressRemainingTime.value = COLLAPSE_DURATION;
	}
}

// Handle mouse leave - start countdown
function handleMouseLeave() {
	isHovered.value = false;
	// Start countdown when mouse leaves
	if (!isCollapsed.value) {
		startCollapseTimer();
	}
}

// Handle toast click to expand/collapse
function handleToastClick() {
	if (isCollapsed.value) {
		isCollapsed.value = false;
		startCollapseTimer();
	}
}

// Collapse the toast manually
function collapseToast() {
	isCollapsed.value = true;
	isPinned.value = false;
	pendingNotificationQueue.value = [];
	resetTimersAndProgress();
	clearPinnedDisplayTimer();
}

// Watch for new notifications and trigger animation
watch(
	toastNotifications,
	(newToasts, oldToasts) => {
		const oldIds = new Set(oldToasts?.map((n) => n.id) || []);
		const added = newToasts.filter((n) => !oldIds.has(n.id));

		if (added.length > 0) {
			const wasEmpty = !oldToasts || oldToasts.length === 0;

			if (wasEmpty || isCollapsed.value) {
				// First notification(s) or collapsed - show newest immediately
				isSwitchingToNewNotification.value = true;
				currentIndex.value = 0;
				currentNotificationDisplayedAt.value = Date.now();

				showNewNotificationAnimation.value = false;
				requestAnimationFrame(() => {
					showNewNotificationAnimation.value = true;
					setTimeout(() => {
						showNewNotificationAnimation.value = false;
					}, NEW_NOTIFICATION_ANIMATION_DURATION);
				});

				if (isCollapsed.value) {
					isCollapsed.value = false;
				}

				clearCollapseTimer();
				startCollapseTimer();
			} else {
				// Currently expanded - queue new notifications instead of switching immediately
				const sortedAdded = [...added].sort((a, b) => b.timestamp - a.timestamp);
				pendingNotificationQueue.value.push(...sortedAdded.map((n) => n.id));

				// Adjust currentIndex to keep showing the same notification
				const currentId = oldToasts?.[currentIndex.value]?.id;
				if (currentId) {
					const newIdx = newToasts.findIndex((n) => n.id === currentId);
					if (newIdx !== -1) {
						currentIndex.value = newIdx;
					}
				}

				if (isPinned.value) {
					if (!pinnedDisplayTimeout.value) {
						// No timer running - check how long the current notification has been shown
						const elapsed = Date.now() - currentNotificationDisplayedAt.value;
						if (elapsed >= COLLAPSE_DURATION) {
							// Current notification has been displayed long enough - show next immediately
							showNextPendingNotification();
						} else {
							// Wait for the remaining display time before switching
							const remaining = COLLAPSE_DURATION - elapsed;
							startPinnedDisplayTimer(remaining);
						}
					}
					// Otherwise a timer is already counting down - let it finish
				}
			}
		}

		// Clean up pending queue - remove IDs no longer in the notification list
		if (pendingNotificationQueue.value.length > 0) {
			const currentNotifIds = new Set(newToasts.map((n) => n.id));
			pendingNotificationQueue.value = pendingNotificationQueue.value.filter((id) => currentNotifIds.has(id));
		}

		lastNotificationCount.value = newToasts.length;

		if (currentIndex.value >= newToasts.length) {
			currentIndex.value = 0;
		}
	},
	{ immediate: true },
);

// Watch for current toast ID changes to reset collapse state (avoid triggering on computed recalculation)
watch(
	() => currentToast.value?.id,
	(newId, oldId) => {
		// Only trigger when the actual toast ID changes, not when the computed recalculates
		if (newId && newId !== oldId) {
			// Skip if we're switching to a new notification (handled by the toastNotifications watch)
			if (isSwitchingToNewNotification.value) {
				// Clear the flag after reactivity has settled to prevent race conditions
				nextTick(() => {
					isSwitchingToNewNotification.value = false;
				});
			} else {
				resetToastState();
				startCollapseTimer();
			}
		}
	},
);

function nextToast() {
	if (currentIndex.value < toastNotifications.value.length - 1) {
		currentIndex.value++;
	} else {
		currentIndex.value = 0;
	}
}

function previousToast() {
	if (currentIndex.value > 0) {
		currentIndex.value--;
	} else {
		currentIndex.value = toastNotifications.value.length - 1;
	}
}

function togglePin() {
	isPinned.value = !isPinned.value;
	if (isPinned.value) {
		// When pinned, stop the collapse timer and show full progress
		resetTimersAndProgress();
		if (pendingNotificationQueue.value.length > 0) {
			startPinnedDisplayTimer();
		}
	} else {
		// When unpinned, restart the collapse timer (will cycle through pending on expiry)
		clearPinnedDisplayTimer();
		startCollapseTimer();
	}
}

// Initialize collapse timer on mount if there are existing notifications
onMounted(() => {
	if (toastNotifications.value.length > 0) {
		startCollapseTimer();
	}
});

// Cleanup timer on unmount
onUnmounted(() => {
	resetTimersAndProgress();
	clearPinnedDisplayTimer();
});
</script>

<style scoped lang="scss">
.toast-container {
	position: absolute;
	top: 58px;
	left: calc(100% - var(--questions-sidebar-width) - 515px);
	z-index: 999;
	width: 500px;
	max-width: 500px;
	pointer-events: none;
	display: flex;
	justify-content: flex-end;
}

.toast-card {
	background-color: rgba(var(--v-theme-surface), 0.95);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	position: relative;
	pointer-events: auto;
	transition: all 0.3s ease;
	cursor: default;
	width: 100%;
	max-width: 500px;

	/* Prevent Vuetify hover overlay from lightening dark colors */
	:deep(.v-alert__underlay),
	:deep(.v-alert .v-alert__overlay) {
		display: none !important;
	}

	/* Reduce horizontal padding on the alert */
	.v-alert {
		padding-left: 12px !important;
		padding-right: 12px !important;
	}

	/* When carousel zones are visible, add padding so text avoids them */
	&.has-carousel {
		.v-alert {
			padding-left: 68px !important;
			padding-right: 68px !important;
		}
	}

	/* Disable Vuetify card hover effects when not collapsed */
	&:not(.collapsed) {
		&:hover,
		&:focus,
		&:active {
			transform: none !important;
			filter: none !important;
		}

		/* Enable text selection for content */
		.toast-title,
		.toast-content,
		.v-alert__text {
			user-select: text;
			cursor: text;
		}
	}

	&.collapsed {
		cursor: pointer;
		opacity: 0.7;
		width: 200px;
		transform-origin: top right;

		.v-alert {
			padding-bottom: 8px !important;
		}

		&:hover {
			opacity: 1;
		}

		/* Disable text selection when collapsed */
		.toast-title,
		.toast-content,
		.v-alert__text {
			user-select: none;
		}
	}

	&:hover:not(.collapsed) {
		.collapse-progress-bar {
			opacity: 0;
			transition: none;
		}

		/* Prevent any hover effects on the alert - ensure background stays consistent */
		:deep(.v-alert) {
			background-color: var(--notification-color) !important;
			border-color: var(--notification-color) !important;
		}
	}

	&.new-notification {
		animation: pulseGlow 0.75s ease-out;

		&::before {
			content: "";
			position: absolute;
			top: -3px;
			left: -3px;
			right: -3px;
			bottom: -3px;
			background: linear-gradient(
				45deg,
				var(--notification-color),
				color-mix(in srgb, var(--notification-color) 80%, white)
			);
			border-radius: inherit;
			z-index: -1;
			opacity: 0;
			animation: glowBorder 0.75s ease-out;
		}
	}
}

.collapse-progress-bar {
	position: absolute;
	bottom: 0;
	left: 0;
	height: 3px;
	width: var(--progress-width);
	background: var(--contrast-color);
	border-radius: 0 0 0 0;
	transition: width 0.05s linear, opacity 0.3s ease;
	z-index: 1;
	opacity: 0.8;
}

.toast-content {
	overflow: hidden;

	&.collapsed-content {
		max-height: 0;
		opacity: 0;
		margin: 0 !important;
		padding: 0 !important;
		transition: all 0.3s ease;
	}
}

@keyframes pulseGlow {
	0% {
		transform: scale(1);
		filter: brightness(1);
	}
	50% {
		transform: scale(1.02);
		filter: brightness(1.1);
	}
	100% {
		transform: scale(1);
		filter: brightness(1);
	}
}

@keyframes glowBorder {
	0% {
		opacity: 0;
		transform: scale(0.95);
	}
	50% {
		opacity: 0.8;
		transform: scale(1.05);
	}
	100% {
		opacity: 0;
		transform: scale(1.1);
	}
}
.toast-title {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.pin-btn {
	&--active {
		:deep(.v-icon) {
			color: var(--contrast-color) !important;
			opacity: 1;
		}
	}
}

.carousel-zone {
	position: absolute;
	top: 0;
	bottom: 0;
	width: 60px;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: var(--contrast-color);
	opacity: 1;
	transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);

	// MD ripple-like overlay on hover
	&::before {
		content: "";
		position: absolute;
		inset: 4px 0;
		background: currentColor;
		opacity: 0;
		border-radius: 6px;
		transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
		pointer-events: none;
	}

	&:hover {
		opacity: 1;

		&::before {
			opacity: 0.08;
		}
	}

	&:active {
		opacity: 1;

		&::before {
			opacity: 0.16;
		}

		.v-icon {
			transform: scale(0.85);
		}
	}

	.v-icon {
		transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
	}

	&--left {
		left: 0;
		border-radius: 8px 0 0 8px;
	}

	&--right {
		right: 0;
		border-radius: 0 8px 8px 0;
	}

	&--disabled {
		opacity: 0.15;
		pointer-events: none;
	}
}

.toast-counter {
	text-align: right;
	color: var(--contrast-color);
	opacity: 0.8;
	font-size: 1rem !important;
}
</style>
